import {
  Delete as DeleteIcon,
  Edit as EditIcon,
  MoreVert as MoreVertIcon,
} from "@mui/icons-material";
import { Tooltip } from "@mui/material";
import { MenuButton, MenuItemWithIcon, ModalOpenButton } from "@msys/ui";
import { useTranslate } from "@tolgee/react";
import { BrandPreferenceLineFragment } from "../BrandPreferences.generated.js";
import { BrandLineAvailabilityScope } from "../brandPreferenceOptions.js";
import { EditBrandLinesModal } from "../modals/EditBrandLinesModal.js";
import { organisationPermissionReason } from "./PreferenceMoveButton.js";

interface Props {
  brand: { id: string; title: string };
  lines: readonly BrandPreferenceLineFragment[];
  /** Where the lines offered for editing must be available; `null` offers removal only. */
  linesAvailableIn: BrandLineAvailabilityScope | null;
  /** False for an organisation item without `pim_catalogue_update`. */
  mayWrite: boolean;
  disabled: boolean;
  onSaveLines: (brandLineIds: string[]) => void;
  onRemove: () => void;
}

/** The actions of one brand preference, at the scope of the item it sits on. */
export function BrandPreferenceItemActions({
  brand,
  lines,
  linesAvailableIn,
  mayWrite,
  disabled,
  onSaveLines,
  onRemove,
}: Props) {
  const { t } = useTranslate(["Product", "Global"]);

  const label = `${brand.title}: ${t("Actions", { ns: "Global" })}`;
  const items = [
    ...(linesAvailableIn !== null
      ? [
          <ModalOpenButton
            key="edit"
            Modal={EditBrandLinesModal}
            modalProps={{
              brand,
              lines,
              availableIn: linesAvailableIn,
              onSave: onSaveLines,
            }}
            disabled={!mayWrite}
          >
            <MenuItemWithIcon icon={<EditIcon />} disabled={!mayWrite}>
              {t("Edit lines", { ns: "Product" })}
            </MenuItemWithIcon>
          </ModalOpenButton>,
        ]
      : []),
    <MenuItemWithIcon
      key="remove"
      icon={<DeleteIcon />}
      disabled={!mayWrite}
      onClick={onRemove}
    >
      {t("Remove preference", { ns: "Product" })}
    </MenuItemWithIcon>,
  ];

  return (
    <MenuButton
      Icon={<MoreVertIcon fontSize="small" />}
      tooltip={label}
      buttonProps={{
        size: "small",
        color: "primary",
        disabled,
        "aria-label": label,
      }}
    >
      {mayWrite
        ? items
        : items.map(item => (
            <Tooltip key={item.key} title={organisationPermissionReason(t)}>
              <div>{item}</div>
            </Tooltip>
          ))}
    </MenuButton>
  );
}
